/**
 * Recovery from stale code-split chunks after a deploy.
 *
 * Routes in `routes.ts` are lazy — each navigation fetches a hashed chunk like
 * `/assets/CollectionsPage-<hash>.js`. Cloudflare Pages only serves the current
 * deployment's assets, and `public/_redirects` sends every unmatched path to
 * `/index.html`, so a tab still running the previous build gets HTML back where
 * it expected JavaScript. The import rejects with "Failed to fetch dynamically
 * imported module" (Chrome) / "error loading dynamically imported module"
 * (Firefox) / "Importing a module script failed" (Safari), and — because the
 * old index.html is pinned to old hashes — every subsequent navigation fails
 * the same way while the already-loaded parts of the app keep working.
 *
 * The escape hatch is to get the tab onto the current build: drop the app shell
 * the stale hashes came from, then reload. Guarded so a chunk that is genuinely
 * missing can't put the page in a reload loop.
 */

const RELOAD_KEY = "burrow:chunk-reload";
/** A second failure inside this window means reloading didn't help — stop. */
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|dynamically imported module/i;

/** True if `value` looks like a failed dynamic import of an app chunk. */
export function isChunkLoadError(value: unknown): boolean {
  if (!value) return false;
  const message =
    value instanceof Error
      ? `${value.name}: ${value.message}`
      : typeof value === "string"
        ? value
        : typeof (value as { message?: unknown }).message === "string"
          ? String((value as { message: string }).message)
          : "";
  return CHUNK_ERROR_PATTERN.test(message);
}

function recentlyAttempted(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    return Date.now() - last < RELOAD_COOLDOWN_MS;
  } catch {
    // Private mode / storage disabled — better to skip the reload than risk a
    // loop we have no way to remember we started.
    return true;
  }
}

function markAttempt(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* nothing we can do; recentlyAttempted() already refuses in this case */
  }
}

/**
 * The precached index.html is what pins this tab to the old chunk hashes, so a
 * plain reload can serve the same broken HTML straight back. Unregister the
 * worker and delete the app-shell caches first — both come back on the next
 * load. Supabase data caches are left alone: they aren't stale, and dropping
 * them would cost the user their offline reads for no reason.
 */
async function purgeAppShell(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* best effort */
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((key) => !key.startsWith("supabase-"))
          .map((key) => caches.delete(key).catch(() => false))
      );
    }
  } catch {
    /* best effort */
  }
}

/**
 * Reload onto the current build. Returns false if we already tried recently —
 * the caller should then surface the error instead of retrying forever.
 */
export async function recoverFromChunkError(): Promise<boolean> {
  if (recentlyAttempted()) return false;
  markAttempt();
  await purgeAppShell();
  window.location.reload();
  return true;
}

/**
 * Catch chunk failures that don't come through a route loader — Vite's own
 * `vite:preloadError`, plus any stray rejected import elsewhere in the app.
 * Call once at boot.
 */
export function installChunkRecovery(): void {
  window.addEventListener("vite:preloadError", ((event: Event) => {
    // Stop Vite rethrowing this as an unhandled error while the reload is in
    // flight — we are handling it.
    event.preventDefault();
    void recoverFromChunkError();
  }) as EventListener);

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) void recoverFromChunkError();
  });
}
